"use client";

import { Show, SignInButton, UserButton } from "@clerk/nextjs";

import { motion } from "framer-motion";
import {
  ArrowUpRight,
  BarChart3,
  Boxes,
  CheckCircle2,
  Cpu,
  Database,
  FileText,
  Layers,
  LineChart,
  Network,
  Shield,
  ShieldCheck,
  Sparkles,
  Workflow,
} from "lucide-react";
import Image from "next/image";
import Link from "next/link";
import { useTheme } from "next-themes";
import * as React from "react";
import { Logo } from "@/components/Logo";
import { MainMenu } from "@/components/MainMenu";
import { ModeToggle } from "@/components/ModeToggle";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import collabImage from "@/public/images/collab.jpg";

const CONTACT_URL = "https://calendar.app.google/4E1JwtdJMcbrdG1D8";

const engines = [
  {
    name: "Kautious Extract",
    role: "Documents",
    blurb:
      "Turn messy PDFs, filings, web pages, tables, and transcripts into quality-scored, source-grounded JSON.",
    href: "/extract",
    icon: FileText,
  },
  {
    name: "Kautious Graph",
    role: "Memory & knowledge",
    blurb:
      "A temporal knowledge graph: entities, relationships, facts, and decisions that know what was true and when.",
    href: "/graph",
    icon: Network,
  },
  {
    name: "Kautious Time",
    role: "Forecasting",
    blurb:
      "Run time-series through 30+ models and ship the one that earns it, with backtested metrics and intervals.",
    href: "/time",
    icon: LineChart,
  },
];

export default function DayttaPage() {
  const { theme } = useTheme();
  const [mounted, setMounted] = React.useState(false);

  React.useEffect(() => {
    setMounted(true);
  }, []);

  const isLight = mounted && theme === "light";

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Header */}
      <header className="sticky top-0 w-full z-50 bg-background/95 dark:bg-background/80 backdrop-blur-xl border-b border-border shadow-sm">
        <nav className="max-w-screen-xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-8">
            <Logo />
            <div className="hidden md:block">
              <MainMenu />
            </div>
          </div>
          <div className="flex items-center gap-4">
            <div className="md:hidden">
              <MainMenu />
            </div>
            <Show when="signed-out">
              <SignInButton mode="modal">
                <Button variant="outline" className="shadow-sm">
                  Sign In
                </Button>
              </SignInButton>
            </Show>
            <Show when="signed-in">
              <UserButton />
              <Link href="/console">
                <Button variant="default" className="shadow-sm">
                  Launch App
                </Button>
              </Link>
            </Show>
            <ModeToggle />
          </div>
        </nav>
      </header>

      <main className="flex-grow">
        {/* Hero Section with Background */}
        <section className="relative w-full overflow-hidden min-h-[85vh] flex items-center justify-center">
          <div className="absolute inset-0 z-0">
            <Image
              src={collabImage}
              alt="Data analytics platform visualization"
              className="object-cover object-center w-full h-full opacity-40 dark:opacity-30"
              fill
              priority
              sizes="100vw"
              quality={90}
            />
            <div className="absolute inset-0 bg-gradient-to-b from-blue-900/20 via-indigo-900/40 to-background/95" />
          </div>

          <div className="absolute inset-0 bg-grid-white/10 [mask-image:radial-gradient(white,transparent_80%)] pointer-events-none z-10 opacity-30" />

          {/* Floating Animated Icons */}
          <motion.div
            className="absolute top-20 left-8 w-16 h-16 bg-blue-500/10 rounded-xl hidden md:flex items-center justify-center animate-float backdrop-blur-sm border border-blue-500/20 z-10"
            initial={{ opacity: 0, scale: 0 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, delay: 0.3 }}
          >
            <Database className="w-8 h-8 text-blue-500/70" />
          </motion.div>
          <motion.div
            className="absolute top-32 right-12 w-14 h-14 bg-indigo-500/10 rounded-full hidden md:flex items-center justify-center animate-float backdrop-blur-sm border border-indigo-500/20 z-10"
            style={{ animationDelay: "1s" }}
            initial={{ opacity: 0, scale: 0 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, delay: 0.4 }}
          >
            <Layers className="w-7 h-7 text-indigo-500/70" />
          </motion.div>
          <motion.div
            className="absolute bottom-32 left-16 w-14 h-14 bg-cyan-500/10 rounded-xl hidden md:flex items-center justify-center animate-float backdrop-blur-sm border border-cyan-500/20 z-10"
            style={{ animationDelay: "2s" }}
            initial={{ opacity: 0, scale: 0 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, delay: 0.5 }}
          >
            <BarChart3 className="w-7 h-7 text-cyan-500/70" />
          </motion.div>
          <motion.div
            className="absolute bottom-20 right-20 w-14 h-14 bg-sky-500/10 rounded-full hidden md:flex items-center justify-center animate-float backdrop-blur-sm border border-sky-500/20 z-10"
            style={{ animationDelay: "0.5s" }}
            initial={{ opacity: 0, scale: 0 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, delay: 0.6 }}
          >
            <Workflow className="w-7 h-7 text-sky-500/70" />
          </motion.div>

          <div className="relative z-20 section-container">
            <div className="flex flex-col items-center text-center max-w-5xl mx-auto px-4 py-20">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
              >
                <span
                  className="inline-flex items-center px-5 py-2.5 rounded-full text-sm font-medium mb-6 backdrop-blur-sm"
                  style={{
                    backgroundColor: isLight
                      ? "rgba(17, 24, 39, 0.2)"
                      : "rgba(255, 255, 255, 0.2)",
                    color: isLight ? "#111827" : "#ffffff",
                    borderWidth: "1px",
                    borderStyle: "solid",
                    borderColor: isLight
                      ? "rgba(17, 24, 39, 0.3)"
                      : "rgba(255, 255, 255, 0.3)",
                  }}
                >
                  <Database
                    className="w-4 h-4 mr-2"
                    style={{ color: isLight ? "#111827" : "#ffffff" }}
                  />
                  The data platform
                </span>
                <h1
                  className="mb-6"
                  style={{ color: isLight ? "#111827" : "#ffffff" }}
                >
                  Turn raw data into decisions you can defend.
                </h1>
                <p
                  className="text-xl mb-6 leading-relaxed max-w-4xl"
                  style={{
                    color: isLight ? "#374151" : "rgba(255, 255, 255, 0.9)",
                  }}
                >
                  Daytta is the data-heavy analytics platform. It composes three
                  engines — Extract for documents, Graph for memory and
                  knowledge, and Time for forecasting — on the governed Llamox
                  runtime, turning unstructured source material into structured,
                  auditable data products.
                </p>
                <p
                  className="text-sm mb-10"
                  style={{
                    color: isLight ? "#4b5563" : "rgba(255, 255, 255, 0.7)",
                  }}
                >
                  Financial markets is our first example. The platform is built
                  to be customized to any data-heavy domain.
                </p>
                <div className="flex flex-col sm:flex-row justify-center gap-4">
                  <Button
                    size="lg"
                    className="hover:opacity-90"
                    style={{
                      backgroundColor: isLight ? "#2563eb" : "#3b82f6",
                      color: "#ffffff",
                    }}
                    asChild
                  >
                    <a href={CONTACT_URL} target="_blank" rel="noreferrer">
                      Request access
                    </a>
                  </Button>
                  <Button
                    size="lg"
                    variant="outline"
                    className="hover:opacity-80"
                    style={{
                      borderColor: isLight ? "#111827" : "#ffffff",
                      color: isLight ? "#111827" : "#ffffff",
                      backgroundColor: "transparent",
                    }}
                    asChild
                  >
                    <Link href="#engines">See the engines</Link>
                  </Button>
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* The Problem */}
        <section className="section-padding bg-secondary/5">
          <div className="section-container max-w-4xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              Your data is trapped in documents, notebooks, and dashboards.
            </h2>
            <p className="text-lg text-muted-foreground mb-4">
              Every data-heavy team rebuilds the same brittle plumbing: a parser
              for the PDFs, a notebook for the model, a vector store standing in
              for memory, and a spreadsheet to glue it together. Then someone
              asks where a number came from, and nobody has a clean answer.
            </p>
            <p className="text-lg text-foreground">
              Daytta is built on the opposite assumption: useful analytics has
              to be observable, repeatable, governed, and reviewable from the
              start — so the work survives scrutiny long after it ships.
            </p>
          </div>
        </section>

        {/* The Engines */}
        <section id="engines" className="section-padding">
          <div className="section-container">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold mb-4">
                Three engines, one data platform.
              </h2>
              <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
                Each engine is a serious product in its own right. Daytta
                composes them — and each is available directly to teams that
                want just one.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto">
              {engines.map((engine) => {
                const Icon = engine.icon;
                return (
                  <Link key={engine.name} href={engine.href} className="group">
                    <Card className="p-8 h-full card-hover border-l-4 border-l-blue-500">
                      <div className="w-12 h-12 bg-blue-500/10 rounded-xl flex items-center justify-center mb-4">
                        <Icon className="w-6 h-6 text-blue-600" />
                      </div>
                      <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-blue-600 mb-2">
                        {engine.role}
                      </p>
                      <h3 className="text-xl font-semibold mb-3">
                        {engine.name}
                      </h3>
                      <p className="text-muted-foreground leading-relaxed mb-6">
                        {engine.blurb}
                      </p>
                      <span className="inline-flex items-center text-sm font-medium text-blue-600">
                        Explore {engine.name}
                        <ArrowUpRight className="w-4 h-4 ml-1 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                      </span>
                    </Card>
                  </Link>
                );
              })}
            </div>
          </div>
        </section>

        {/* Powered by Llamox */}
        <section className="section-padding bg-muted/30">
          <div className="section-container max-w-5xl mx-auto">
            <div className="text-center mb-16">
              <div className="w-12 h-12 bg-violet-500/10 rounded-xl flex items-center justify-center mx-auto mb-6">
                <Cpu className="w-6 h-6 text-violet-600" />
              </div>
              <h2 className="text-3xl md:text-4xl font-bold mb-4">
                Governed by Llamox, end to end.
              </h2>
              <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
                Daytta runs on the Llamox control plane, so every pipeline,
                model run, and data product comes with the controls a serious
                team needs.
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {[
                {
                  icon: Workflow,
                  title: "Approval workflows",
                  body: "High-impact steps pause for human review before they reach production.",
                },
                {
                  icon: Shield,
                  title: "Sandboxed execution",
                  body: "Generated code and analysis run in isolated providers, never against raw infrastructure.",
                },
                {
                  icon: ShieldCheck,
                  title: "Audit trails",
                  body: "Inputs, transformations, decisions, and lineage are recorded as the work moves.",
                },
                {
                  icon: Layers,
                  title: "Versioning & rollback",
                  body: "Promote data products through staged deployment with health checks and rollback.",
                },
              ].map((item) => {
                const Icon = item.icon;
                return (
                  <div key={item.title} className="flex items-start gap-4">
                    <div className="w-10 h-10 bg-violet-500/10 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Icon className="w-5 h-5 text-violet-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold mb-1.5">{item.title}</h3>
                      <p className="text-muted-foreground text-sm">
                        {item.body}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Financial markets, first */}
        <section className="section-padding">
          <div className="section-container max-w-5xl mx-auto">
            <div className="text-center mb-12 max-w-3xl mx-auto">
              <p className="text-xs font-semibold tracking-[0.25em] uppercase text-muted-foreground/70 mb-4">
                Our first example
              </p>
              <h2 className="text-3xl md:text-4xl font-bold mb-4">
                Financial markets, end to end.
              </h2>
              <p className="text-lg text-muted-foreground">
                We proved the platform where wrong answers get expensive. The
                same engines, pipelines, and governance generalize to any
                data-heavy domain.
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {[
                {
                  title: "Extract the filings",
                  body: "Pull XBRL facts, sections, and entities out of SEC filings, transcripts, and corporate actions with source grounding.",
                },
                {
                  title: "Remember the context",
                  body: "Keep entities, relationships, decisions, and timelines in a knowledge graph your workflows can query.",
                },
                {
                  title: "Forecast the numbers",
                  body: "Run demand, revenue, and market series through a model tournament and ship the forecast with receipts.",
                },
              ].map((item) => (
                <Card key={item.title} className="p-6 card-hover">
                  <CheckCircle2 className="w-5 h-5 text-blue-600 mb-3" />
                  <h3 className="font-semibold mb-2">{item.title}</h3>
                  <p className="text-sm text-muted-foreground">{item.body}</p>
                </Card>
              ))}
            </div>
            <p className="text-center text-muted-foreground mt-10 max-w-3xl mx-auto">
              Beyond finance, the same building blocks fit legal, compliance,
              healthcare, operations, and any team drowning in documents and
              time-series. Domain packs and custom pipelines tailor Daytta to
              your data.
            </p>
          </div>
        </section>

        {/* Customizable */}
        <section className="section-padding bg-muted/30">
          <div className="section-container max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold mb-4">
                Built to be customized.
              </h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {[
                {
                  icon: Boxes,
                  title: "Domain packs",
                  body: "Start with templates, schemas, validators, and connectors for your field — or generate a pack from a plain-English description of how your team works.",
                },
                {
                  icon: Workflow,
                  title: "Composable pipelines",
                  body: "Wire Extract, Graph, and Time together with transformations, lineage, quality checks, and schedules into the workflow you actually need.",
                },
                {
                  icon: Database,
                  title: "Your stack, connected",
                  body: "REST, SDK, and MCP interfaces, signed webhooks, and warehouse-ready outputs plug Daytta into the tools you already run.",
                },
              ].map((item) => {
                const Icon = item.icon;
                return (
                  <Card key={item.title} className="p-8 card-hover">
                    <div className="w-12 h-12 bg-blue-500/10 rounded-xl flex items-center justify-center mb-4">
                      <Icon className="w-6 h-6 text-blue-600" />
                    </div>
                    <h3 className="text-xl font-semibold mb-3">{item.title}</h3>
                    <p className="text-muted-foreground leading-relaxed">
                      {item.body}
                    </p>
                  </Card>
                );
              })}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="section-padding bg-gradient-to-br from-blue-600 to-indigo-600 text-white">
          <div className="section-container text-center">
            <div className="max-w-3xl mx-auto">
              <div className="inline-flex items-center gap-2 mb-4 text-white/90">
                <Sparkles className="w-5 h-5" />
                <span className="text-sm font-medium uppercase tracking-[0.2em]">
                  Invite-only early access
                </span>
              </div>
              <h2 className="text-4xl font-semibold mb-6 text-white">
                Get your data out of the prototype phase.
              </h2>
              <p className="text-xl mb-8 text-white/90 leading-relaxed">
                Tell us the data you are fighting with. If it is a fit, we will
                help you stand up a governed, customizable pipeline this week.
              </p>
              <div className="flex flex-col sm:flex-row justify-center gap-4">
                <Button
                  size="lg"
                  variant="secondary"
                  className="bg-white text-blue-600 hover:bg-gray-100"
                  asChild
                >
                  <a href={CONTACT_URL} target="_blank" rel="noreferrer">
                    Request access
                  </a>
                </Button>
                <Button
                  size="lg"
                  variant="outline"
                  className="border-white text-white hover:bg-white/10"
                  asChild
                >
                  <Link href="/kautious">See Kautious, built on Daytta</Link>
                </Button>
              </div>
            </div>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="border-t bg-background/50 backdrop-blur-sm">
        <div className="max-w-screen-xl mx-auto px-6 py-12">
          <div className="flex justify-between items-center">
            <Logo />
            <div className="flex items-center gap-6">
              <Link
                href="/terms"
                className="text-sm text-muted-foreground hover:text-primary"
              >
                Terms
              </Link>
              <Link
                href="/privacy"
                className="text-sm text-muted-foreground hover:text-primary"
              >
                Privacy
              </Link>
              <ModeToggle />
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
